


import { fetchPlanningOrdersByShift } from '@/entities/orders/thunks/fetchPlanningOrdersByShift'
import { fetchUnassignedOrdersByScenario } from '@/entities/orders/thunks/fetchUnassignedOrdersByScenario'
import { selectAllScenariosWithShifts } from '@/entities/schedules'
import { widgetWindowsSelector } from '@/entities/widgetWindows'
import { OperationsMapWidgetWindow } from '@/entities/widgetWindows/models/WidgetWindow'
import { updateWidgetWindow } from '@/entities/widgetWindows/widgetWindowsSlice'
import { MosaicAdditionalToolbar } from '@/shared/components'
import { DropdownOption } from '@/shared/components/FilterMultiselectDropdown/FilterMultiselectDropdown'
import { MosaicToolbarMenuItem } from '@/shared/components/MosaicAdditionalToolbar/models/MosaicToolbarMenuItem'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'

import { useOperationMapContext } from './OperationsMapWidgetContext'

export type PlanningOperationsMapWidgetToolbarProps = {
  //
}

// TODO: Add an API endpoint to retrieve the list of shifts for the dropdown filter
export const PlanningOperationsMapWidgetToolbar: React.FC<PlanningOperationsMapWidgetToolbarProps> = () => {
  const { widgetId } = useOperationMapContext()
  const mapWidget = useAppSelector((state) => widgetWindowsSelector.selectById(state, widgetId))
  const selectedShiftIds = (mapWidget as OperationsMapWidgetWindow)?.shiftIds || []
  const selectedScenarioIds = (mapWidget as OperationsMapWidgetWindow)?.unassignedScheduleIds || []
  const dispatch = useAppDispatch()
  const planningScenariosWithShifts = useAppSelector((state) => selectAllScenariosWithShifts(state))

  // Optional TODO: Create an API endpoint to retrieve the list of scenarios and shifts for the dropdown filter
  const planningShiftOptions: DropdownOption[] = planningScenariosWithShifts.flatMap((scenario) =>
    scenario.shifts.map((shift) => ({ label: `${scenario.name} > ${shift.name}`, value: shift.id })),
  )

  const selectedShiftOptions = selectedShiftIds
    .map((shiftId) => planningShiftOptions.find((option) => option.value === shiftId))
    .filter(option => option !== undefined)

  const planningScenarioOptions: DropdownOption[] = planningScenariosWithShifts.map((scenario) => ({
    label: scenario.name,
    value: scenario.id,
  }))

  const selectedScenarioOptions = selectedScenarioIds
    .map((scenarioId) => planningScenarioOptions.find((option) => option.value === scenarioId))
    .filter(option => option !== undefined)

  const handleSelectShift = (val: DropdownOption) => {
    const index = selectedShiftIds.findIndex(id => id === val.value)

    if (index > -1) {
      const newSelectedShiftIds = [...selectedShiftIds]

      newSelectedShiftIds.splice(index, 1)

      dispatch(updateWidgetWindow({
        id: widgetId,
        changes: {
          shiftIds: newSelectedShiftIds,
        },
      }))
    } else {
      const newSelectedShiftIds = [...selectedShiftIds, val.value]

      dispatch(fetchPlanningOrdersByShift({ shiftId: val.value }))
      dispatch(updateWidgetWindow({
        id: widgetId,
        changes: {
          shiftIds: newSelectedShiftIds,
        },
      }))
    }
  }

  const handleClearAllShifts = () => {
    dispatch(updateWidgetWindow({
      id: widgetId,
      changes: {
        shiftIds: [],
      },
    }))
  }

  const handleSelectUnassignedScenario = (val: DropdownOption) => {
    const index = selectedScenarioIds.findIndex(id => id === val.value)

    if (index > -1) {
      const newSelectedScenarioIds = [...selectedScenarioIds]

      newSelectedScenarioIds.splice(index, 1)

      dispatch(updateWidgetWindow({
        id: widgetId,
        changes: {
          unassignedScheduleIds: newSelectedScenarioIds,
        },
      }))
    } else {
      const newSelectedScenarioIds = [...selectedScenarioIds, val.value]

      dispatch(fetchUnassignedOrdersByScenario({ scenarioId: val.value }))
      dispatch(updateWidgetWindow({
        id: widgetId,
        changes: {
          unassignedScheduleIds: newSelectedScenarioIds,
        },
      }))
    }
  }

  const handleClearUnassignedScenarios = () => {
    dispatch(updateWidgetWindow({
      id: widgetId,
      changes: {
        unassignedScheduleIds: [],
      },
    }))
  }
  
  const toolbarMenuItems: MosaicToolbarMenuItem[] = [
    {
      type: 'multidropdown',
      label: 'Vehicle Shifts',
      values: (selectedShiftOptions as DropdownOption[]),
      options: planningShiftOptions,
      searchActionEnable: true,
      searchActionIcon: faTrash,
      onSelectItem: handleSelectShift,
      slotProps: {
        search: {
          placeholder: 'Search scenario shifts',
        },
        searchAction: {
          onClick: handleClearAllShifts,
          tooltipTitle: 'Clear selected shifts',
          disabled: selectedShiftIds.length === 0,
        },
      },
    },
    {
      type: 'multidropdown',
      label: 'Unassigned Orders',
      values: (selectedScenarioOptions as DropdownOption[]),
      options: planningScenarioOptions,
      searchActionEnable: true,
      searchActionIcon: faTrash,
      onSelectItem: handleSelectUnassignedScenario,
      slotProps: {
        search: {
          placeholder: 'Search scenario',
        },
        searchAction: {
          onClick: handleClearUnassignedScenarios,
          tooltipTitle: 'Clear selected scenarios',
          disabled: selectedScenarioIds.length === 0,
        },
      },
    },
  ]

  return (
    <MosaicAdditionalToolbar
      toolbarMenuItems={toolbarMenuItems}
      averageMenuItemWidth={120}
    />
  )
}
